import type { DailySchedulePlan, ScheduleValidationIssue } from './types';
import { suggestVehiclesForJob } from './suggestVehiclesForJob';
import type { VehicleSuggestion } from './suggestVehiclesForJob';

export interface ScheduleFixJobItem {
  jobId: string;
  title: string;
  fixed: boolean;
  /** Best feasible alternate vehicle (excluding current), if any. */
  recommended?: VehicleSuggestion;
}

export interface ScheduleFixVehicleItem {
  vehicleId: string;
  fleetNumber?: string;
  issues: ScheduleValidationIssue[];
  errors: string[];
  draftJobs: ScheduleFixJobItem[];
  /** Count of published jobs on this vehicle (for warnings; titles not listed in UI). */
  fixedJobCount: number;
}

export interface ScheduleFixReport {
  /** Infeasible chains that include draft jobs — block publish until fixed. */
  blocking: ScheduleFixVehicleItem[];
  /** Infeasible chains that are published-only — warn, do not block publish. */
  publishedWarnings: ScheduleFixVehicleItem[];
}

function jobIdsOnVehicle(plan: DailySchedulePlan, vehicleId: string): string[] {
  const legs = plan.vehicles.find((v) => v.vehicleId === vehicleId)?.legs ?? [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const leg of legs) {
    if (!seen.has(leg.jobId)) {
      seen.add(leg.jobId);
      ids.push(leg.jobId);
    }
  }
  return ids;
}

function isFixedJobOnVehicle(plan: DailySchedulePlan, vehicleId: string, jobId: string): boolean {
  const legs = plan.vehicles.find((v) => v.vehicleId === vehicleId)?.legs ?? [];
  return legs.some((l) => l.jobId === jobId && Boolean(l.isFixed));
}

function jobTitle(plan: DailySchedulePlan, jobId: string, fallbackLabel?: string): string {
  const raw =
    plan.jobTitles?.[jobId] ??
    plan.jobs?.find((j) => j.id === jobId)?.title ??
    plan.unassignedJobs.find((j) => j.id === jobId)?.title ??
    fallbackLabel ??
    jobId;
  // Operators should not see opaque UAT / timestamp titles in primary UI.
  if (/^UAT-|^\d{10,}$/i.test(raw.trim()) || /UAT-UX/i.test(raw)) {
    return fallbackLabel && fallbackLabel !== raw ? fallbackLabel : 'Published trip';
  }
  return raw;
}

function buildVehicleItem(
  plan: DailySchedulePlan,
  vehicle: DailySchedulePlan['vehicles'][number]
): ScheduleFixVehicleItem {
  const draftJobs: ScheduleFixJobItem[] = [];
  let fixedJobCount = 0;

  for (const jobId of jobIdsOnVehicle(plan, vehicle.vehicleId)) {
    const fixed = isFixedJobOnVehicle(plan, vehicle.vehicleId, jobId);
    if (fixed) {
      fixedJobCount += 1;
      continue;
    }
    const title = jobTitle(
      plan,
      jobId,
      vehicle.legs.find((l) => l.jobId === jobId)?.label
    );
    const entry: ScheduleFixJobItem = { jobId, title, fixed: false };
    const suggestions = suggestVehiclesForJob(plan, jobId);
    const recommended = suggestions.find(
      (s) => s.feasible && s.vehicleId !== vehicle.vehicleId
    );
    if (recommended) entry.recommended = recommended;
    draftJobs.push(entry);
  }

  return {
    vehicleId: vehicle.vehicleId,
    fleetNumber: vehicle.fleetNumber,
    issues: vehicle.validation.issues,
    errors: vehicle.validation.errors,
    draftJobs,
    fixedJobCount,
  };
}

/**
 * Partition infeasible vehicles into publish-blocking (has drafts) vs
 * published-only warnings (CONF-20260716-daily-schedule-published-warn).
 */
export function listScheduleFixReport(plan: DailySchedulePlan): ScheduleFixReport {
  const blocking: ScheduleFixVehicleItem[] = [];
  const publishedWarnings: ScheduleFixVehicleItem[] = [];

  for (const vehicle of plan.vehicles) {
    if (vehicle.validation.ok) continue;
    const item = buildVehicleItem(plan, vehicle);
    if (item.draftJobs.length > 0) {
      blocking.push(item);
    } else {
      publishedWarnings.push(item);
    }
  }

  return { blocking, publishedWarnings };
}

/** True when no draft-involving infeasible chains remain. */
export function isSchedulePublishChainOk(plan: DailySchedulePlan): boolean {
  return listScheduleFixReport(plan).blocking.length === 0;
}

/**
 * @deprecated Prefer listScheduleFixReport — returns blocking items only.
 */
export function listScheduleFixItems(plan: DailySchedulePlan): ScheduleFixVehicleItem[] {
  return listScheduleFixReport(plan).blocking;
}
